import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { affiliatesAPI, engageAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnDanger, btnGhost, btnPrimary, EmptyState, Field, inputCls, panelCls, StatusBadge } from '../../components/ui';

const ok = () => toast.success(t('Saved'));
const fail = (err) => toast.error(err.response?.data?.detail || t('Save failed'));

function Section({ title, children }) {
  return (
    <div className={`${panelCls} p-5`}>
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      {children}
    </div>
  );
}

export default function EngagePage() {
  const [anns, setAnns] = useState([]);
  const [assets, setAssets] = useState([]);
  const [contests, setContests] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [lb, setLb] = useState(null);

  const [annForm, setAnnForm] = useState({ title: '', body: '' });
  const [assetTitle, setAssetTitle] = useState('');
  const [assetFile, setAssetFile] = useState(null);
  const [cForm, setCForm] = useState({ title: '', metric: 'referrals', starts_at: '', ends_at: '', prize1: '', pts1: 100 });
  const [msForm, setMsForm] = useState({ metric: 'paid_referrals', threshold: 5, plan_id: '' });
  const [bForm, setBForm] = useState({ name: '', metric: 'paid_referrals', threshold: 5, currency: 'cash', amount: 25 });

  const load = useCallback(() => {
    engageAPI.adminAnnouncements().then(({ data }) => setAnns(data.items)).catch(() => {});
    engageAPI.adminAssets().then(({ data }) => setAssets(data.items)).catch(() => {});
    engageAPI.adminContests().then(({ data }) => setContests(data.items)).catch(() => {});
    engageAPI.milestones().then(({ data }) => setMilestones(data.items)).catch(() => {});
    engageAPI.bonuses().then(({ data }) => setBonuses(data.items)).catch(() => {});
    engageAPI.lbConfig().then(({ data }) => setLb(data)).catch(() => {});
    affiliatesAPI.plans().then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold">{t('Engage')}</h1>

      <Section title={t('Announcements')}>
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div className="w-56"><Field label={t('Title')}><input className={inputCls} value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} /></Field></div>
          <div className="min-w-64 flex-1"><Field label={t('Body')}><input className={inputCls} value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} /></Field></div>
          <button className={btnPrimary} onClick={() => engageAPI.createAnnouncement(annForm).then(() => { setAnnForm({ title: '', body: '' }); ok(); load(); }).catch(fail)}>{t('Publish')}</button>
        </div>
        {anns.length === 0 ? <EmptyState>{t('No announcements yet.')}</EmptyState> : (
          <ul className="space-y-1.5 text-sm">
            {anns.map((a) => (
              <li key={a.id} className="flex items-center gap-3">
                <span className="font-medium">{a.title}</span>
                <span className="flex-1 truncate text-xs text-mms-muted">{a.body}</span>
                <StatusBadge status={a.active ? 'active' : 'inactive'} />
                <button className={btnGhost} onClick={() => engageAPI.updateAnnouncement(a.id, { active: !a.active }).then(load).catch(fail)}>{a.active ? t('Hide') : t('Show')}</button>
                <button className={btnDanger} onClick={() => engageAPI.deleteAnnouncement(a.id).then(load).catch(fail)}>{t('Delete')}</button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={t('Marketing assets')}>
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div className="w-56"><Field label={t('Title')}><input className={inputCls} value={assetTitle} onChange={(e) => setAssetTitle(e.target.value)} /></Field></div>
          <div className="min-w-64 flex-1"><Field label={t('File (max 25MB)')}><input type="file" className={inputCls} onChange={(e) => setAssetFile(e.target.files?.[0] || null)} /></Field></div>
          <button className={btnPrimary} disabled={!assetFile} onClick={() => engageAPI.uploadAsset(assetFile, assetTitle).then(() => { setAssetTitle(''); setAssetFile(null); ok(); load(); }).catch(fail)}>{t('Upload')}</button>
        </div>
        {assets.length === 0 ? <EmptyState>{t('No assets yet.')}</EmptyState> : (
          <ul className="space-y-1.5 text-sm">
            {assets.map((a) => (
              <li key={a.id} className="flex items-center gap-3">
                <a href={a.url} target="_blank" rel="noreferrer" className="font-medium text-mms-accent underline">{a.title}</a>
                <span className="flex-1 text-xs text-mms-faint">{a.filename} · {(a.size / 1024).toFixed(0)} KB</span>
                <button className={btnDanger} onClick={() => engageAPI.deleteAsset(a.id).then(load).catch(fail)}>{t('Delete')}</button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={t('Contests')}>
        <div className="mb-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <Field label={t('Title')}><input className={inputCls} value={cForm.title} onChange={(e) => setCForm({ ...cForm, title: e.target.value })} /></Field>
          <Field label={t('Metric')}>
            <select className={inputCls} value={cForm.metric} onChange={(e) => setCForm({ ...cForm, metric: e.target.value })}>
              {['clicks', 'referrals', 'paid_referrals', 'points'].map((m) => <option key={m} value={m}>{t(m)}</option>)}
            </select>
          </Field>
          <Field label={t('Starts (ISO)')}><input className={inputCls} placeholder="2026-07-15T00:00:00+00:00" value={cForm.starts_at} onChange={(e) => setCForm({ ...cForm, starts_at: e.target.value })} /></Field>
          <Field label={t('Ends (ISO)')}><input className={inputCls} value={cForm.ends_at} onChange={(e) => setCForm({ ...cForm, ends_at: e.target.value })} /></Field>
          <Field label={t('1st prize label')}><input className={inputCls} value={cForm.prize1} onChange={(e) => setCForm({ ...cForm, prize1: e.target.value })} /></Field>
          <Field label={t('1st prize points')}><input type="number" className={inputCls} value={cForm.pts1} onChange={(e) => setCForm({ ...cForm, pts1: e.target.value })} /></Field>
        </div>
        <button className={btnPrimary} onClick={() => engageAPI.createContest({
          title: cForm.title, metric: cForm.metric, starts_at: cForm.starts_at, ends_at: cForm.ends_at,
          status: 'live', prizes: [{ label: cForm.prize1 || '1st place', points: Number(cForm.pts1 || 0) }],
        }).then(() => { ok(); load(); }).catch(fail)}>{t('Launch contest')}</button>

        <div className="mt-4 space-y-2">
          {contests.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-medium">{c.title}</span>
              <code className="text-xs text-mms-muted">{c.metric}</code>
              <span className="text-xs text-mms-faint">{(c.starts_at || '').slice(0, 10)} → {(c.ends_at || '').slice(0, 10)}</span>
              <StatusBadge status={c.status} />
              {c.status === 'finished' && c.winners?.length > 0 && (
                <span className="text-xs text-mms-muted">{t('winner')}: {c.winners[0].member?.name || c.winners[0].mid}</span>
              )}
              {c.status !== 'finished' && (
                <>
                  <button className={btnGhost} onClick={() => engageAPI.finishContest(c.id).then(() => { toast.success(t('Contest finished — prizes awarded')); load(); }).catch(fail)}>{t('Finish & award')}</button>
                  <button className={btnDanger} onClick={() => engageAPI.deleteContest(c.id).then(load).catch(fail)}>{t('Delete')}</button>
                </>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title={t('Milestones (auto plan upgrades)')}>
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <Field label={t('Metric')}>
            <select className={`${inputCls} !w-40`} value={msForm.metric} onChange={(e) => setMsForm({ ...msForm, metric: e.target.value })}>
              {['referrals', 'paid_referrals', 'revenue'].map((m) => <option key={m} value={m}>{t(m)}</option>)}
            </select>
          </Field>
          <Field label={t('Threshold')}><input type="number" className={`${inputCls} !w-28`} value={msForm.threshold} onChange={(e) => setMsForm({ ...msForm, threshold: e.target.value })} /></Field>
          <Field label={t('Upgrade to plan')}>
            <select className={`${inputCls} !w-52`} value={msForm.plan_id} onChange={(e) => setMsForm({ ...msForm, plan_id: e.target.value })}>
              <option value="">—</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <button className={btnPrimary} disabled={!msForm.plan_id} onClick={() => engageAPI.createMilestone({ ...msForm, threshold: Number(msForm.threshold) }).then(() => { ok(); load(); }).catch(fail)}>{t('Add')}</button>
        </div>
        {milestones.map((m) => (
          <div key={m.id} className="flex items-center gap-3 text-sm">
            <span>{t(m.metric)} ≥ <span className="mms-num">{m.threshold}</span> → {m.plan_name}</span>
            <button className={btnDanger} onClick={() => engageAPI.deleteMilestone(m.id).then(load).catch(fail)}>{t('Delete')}</button>
          </div>
        ))}
      </Section>

      <Section title={t('Performance bonuses')}>
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <Field label={t('Name')}><input className={`${inputCls} !w-44`} value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} /></Field>
          <Field label={t('Metric')}>
            <select className={`${inputCls} !w-40`} value={bForm.metric} onChange={(e) => setBForm({ ...bForm, metric: e.target.value })}>
              {['referrals', 'paid_referrals', 'revenue'].map((m) => <option key={m} value={m}>{t(m)}</option>)}
            </select>
          </Field>
          <Field label={t('Threshold')}><input type="number" className={`${inputCls} !w-24`} value={bForm.threshold} onChange={(e) => setBForm({ ...bForm, threshold: e.target.value })} /></Field>
          <Field label={t('Currency')}>
            <select className={`${inputCls} !w-28`} value={bForm.currency} onChange={(e) => setBForm({ ...bForm, currency: e.target.value })}>
              {['cash', 'credit', 'points'].map((c) => <option key={c} value={c}>{t(c)}</option>)}
            </select>
          </Field>
          <Field label={t('Amount')}><input type="number" className={`${inputCls} !w-24`} value={bForm.amount} onChange={(e) => setBForm({ ...bForm, amount: e.target.value })} /></Field>
          <button className={btnPrimary} onClick={() => engageAPI.createBonus({ ...bForm, threshold: Number(bForm.threshold), amount: Number(bForm.amount) }).then(() => { ok(); load(); }).catch(fail)}>{t('Add')}</button>
        </div>
        {bonuses.map((b) => (
          <div key={b.id} className="flex items-center gap-3 text-sm">
            <span className="font-medium">{b.name}</span>
            <span className="text-xs text-mms-muted">{t(b.metric)} ≥ {b.threshold} → {b.award?.amount} {t(b.award?.currency)}</span>
            <button className={btnDanger} onClick={() => engageAPI.deleteBonus(b.id).then(load).catch(fail)}>{t('Delete')}</button>
          </div>
        ))}
      </Section>

      {lb && (
        <Section title={t('Public leaderboard')}>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex items-center gap-2 self-center text-sm text-mms-muted">
              <input type="checkbox" checked={lb.public_enabled} onChange={(e) => setLb({ ...lb, public_enabled: e.target.checked })} />
              {t('Enabled (public shareable page at /leaderboard)')}
            </label>
            <select className={`${inputCls} !w-40`} value={lb.public_metric} onChange={(e) => setLb({ ...lb, public_metric: e.target.value })}>
              {['points', 'xp', 'referrals', 'paid_referrals', 'clicks', 'revenue'].map((m) => <option key={m} value={m}>{t(m)}</option>)}
            </select>
            <select className={`${inputCls} !w-32`} value={lb.public_period} onChange={(e) => setLb({ ...lb, public_period: e.target.value })}>
              {['7d', '30d', '90d', 'all'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <button className={btnPrimary} onClick={() => engageAPI.putLbConfig(lb).then(() => ok()).catch(fail)}>{t('Save')}</button>
          </div>
        </Section>
      )}
    </div>
  );
}
